import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import MarketGrowthAdminApi from "../../../../../infrastructure/api/marketGrowthAdmin";
import { formatMoney } from "../../../partner-commission/utils/formatMoney";
import {
  PARTNER_COMMISSION_STATUS_LABELS,
  getCommissionStatusChipColor,
} from "../../../partner-commission/constants/portalEnums";
import MarketGrowthDataTable from "./MarketGrowthDataTable";
import { getEnumLabel, TRIGGER_EVENT_LABELS } from "../constants/commissionEnums";

const Field = ({ label, value }) => (
  <>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ mb: 1.5 }}>
      {value ?? "—"}
    </Typography>
  </>
);

const formatDate = (value) =>
  value ? new Date(value).toLocaleString("fa-IR") : "—";

const AdminCommissionDetailDialog = ({ open, onClose, commissionId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!commissionId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await MarketGrowthAdminApi.getCommissionById(commissionId);
      const normalized = MarketGrowthAdminApi.normalize(
        res,
        "بارگذاری جزئیات پورسانت ناموفق بود."
      );
      if (normalized.error) {
        setError(normalized.error);
        setData(null);
        return;
      }
      setData(normalized.data);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, [commissionId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>جزئیات پورسانت #{commissionId}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : data ? (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Field label="شناسه نماینده" value={data.partnerId} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="نماینده" value={data.partnerDisplayName || data.partnerId} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="نماینده مبدأ" value={data.sourcePartnerId ?? "—"} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="گروه پورسانتی" value={data.commissionGroupTitle || data.commissionGroupId} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="قانون پورسانت" value={data.ruleTitle || data.sourceRuleId} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="شناسه کمپین" value={data.campaignId ?? "—"} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="کاربر مرتبط" value={data.relatedUserId ?? "—"} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="پروژه مرتبط" value={data.relatedProjectId ?? "—"} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  label="رویداد"
                  value={getEnumLabel(TRIGGER_EVENT_LABELS, data.triggerEvent)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  وضعیت
                </Typography>
                <Box sx={{ mb: 1.5 }}>
                  <Chip
                    size="small"
                    label={getEnumLabel(PARTNER_COMMISSION_STATUS_LABELS, data.status)}
                    color={getCommissionStatusChipColor(data.status)}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="مبلغ مبنا" value={formatMoney(data.baseAmount)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="مبلغ پورسانت" value={formatMoney(data.commissionAmount)} />
              </Grid>
              <Grid item xs={12}>
                <Field label="توضیحات" value={data.description} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field label="تاریخ ایجاد" value={formatDate(data.createdAt || data.calculatedAt)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field label="تاریخ تأیید" value={formatDate(data.approvedAt)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field label="تاریخ پرداخت" value={formatDate(data.paidAt)} />
              </Grid>
              {data.parentCommissionId ? (
                <>
                  <Grid item xs={12} sm={6}>
                    <Field label="پورسانت والد" value={data.parentCommissionId} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      label="مبلغ پورسانت والد"
                      value={formatMoney(data.parentCommissionAmount)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field label="سهم تقسیم‌شده" value={formatMoney(data.childSplitAmount)} />
                  </Grid>
                </>
              ) : null}
            </Grid>

            {data.childSplits?.length ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  تقسیم به زیرمجموعه
                </Typography>
                <MarketGrowthDataTable
                  rowKey="id"
                  rows={data.childSplits.map((split, index) => ({
                    ...split,
                    id: `${split.partnerId}-${index}`,
                    amountLabel: formatMoney(split.commissionAmount),
                    statusLabel: getEnumLabel(
                      PARTNER_COMMISSION_STATUS_LABELS,
                      split.status
                    ),
                  }))}
                  headCells={[
                    { id: "partnerId", label: "نماینده" },
                    { id: "amountLabel", label: "مبلغ" },
                    { id: "statusLabel", label: "وضعیت", type: "chip" },
                  ]}
                  showFilter={false}
                  showIndex={false}
                  showPaging={false}
                  tableMinWidth="auto"
                />
              </Box>
            ) : null}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>بستن</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminCommissionDetailDialog;
